import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/ui/Button";
import { ClientCombobox } from "@/ui/ClientCombobox";
import { Field, Modal, fieldInputClass } from "@/ui/modals/Modal";
import { useLabels } from "@/lib/labels";
import type { Payment, PaymentMethod, PaymentStatus, PaymentType } from "@/types";
import type { PaymentInput } from "./payments.adapter";

interface PaymentFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: PaymentInput) => void | Promise<void>;
  clients: { id: string; name: string }[];
  initial?: Payment;
  saving?: boolean;
}

export function PaymentFormModal({ open, onClose, onSave, clients, initial, saving }: PaymentFormModalProps) {
  const { PAYMENT_METHOD, PAYMENT_STATUS, PAYMENT_TYPE } = useLabels();
  const { t } = useTranslation();
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [type, setType] = useState<PaymentType>("FEE");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("CASH");
  const [status, setStatus] = useState<PaymentStatus>("PENDING");
  const [period, setPeriod] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [amountError, setAmountError] = useState("");
  const [clientError, setClientError] = useState("");

  useEffect(() => {
    if (!open) return;
    setClientId(initial?.clientId ?? "");
    setClientName(initial?.clientName ?? "");
    setType(initial?.type ?? "FEE");
    setAmount(initial ? String(initial.amount) : "");
    setMethod(initial?.method ?? "CASH");
    setStatus(initial?.status ?? "PENDING");
    setPeriod(initial?.period ?? "");
    setDueDate(initial?.dueDate ?? "");
    setNote(initial?.note ?? "");
    setAmountError("");
    setClientError("");
  }, [initial, open]);

  const submit = () => {
    const value = Number(amount);
    if (!clientId) {
      setClientError(t("clientCombobox.required"));
      return;
    }
    setClientError("");
    if (!value || value <= 0) {
      setAmountError(t("payments.form.amountInvalid"));
      return;
    }
    setAmountError("");
    const selectedClient = clients.find((client) => client.id === clientId);
    void onSave({
      clientId,
      clientName: selectedClient?.name ?? clientName,
      type,
      amount: value,
      method,
      status,
      period: period.trim() || undefined,
      dueDate: dueDate || undefined,
      note: note.trim() || undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? t("payments.form.editTitle") : t("payments.form.addTitle")}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button size="sm" onClick={submit} loading={saving}>
            {initial ? t("payments.form.save") : t("payments.form.add")}
          </Button>
        </>
      }
    >
      <Field label={t("payments.form.client")} required error={clientError}>
        <ClientCombobox
          options={clients}
          value={clientId}
          onChange={(id, name) => {
            setClientId(id);
            setClientName(name);
            setClientError("");
          }}
          nullable={false}
        />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("payments.form.type")} required>
          <select className={fieldInputClass} value={type} onChange={(event) => setType(event.target.value as PaymentType)}>
            {(Object.keys(PAYMENT_TYPE) as PaymentType[]).map((paymentType) => (
              <option key={paymentType} value={paymentType}>
                {PAYMENT_TYPE[paymentType].label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("payments.form.method")} required>
          <select className={fieldInputClass} value={method} onChange={(event) => setMethod(event.target.value as PaymentMethod)}>
            {(Object.keys(PAYMENT_METHOD) as PaymentMethod[]).map((paymentMethod) => (
              <option key={paymentMethod} value={paymentMethod}>
                {PAYMENT_METHOD[paymentMethod].label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("payments.form.amount")} required error={amountError}>
          <input
            type="number"
            min={0}
            step={1}
            className={fieldInputClass}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        <Field label={t("payments.form.status")} required>
          <select className={fieldInputClass} value={status} onChange={(event) => setStatus(event.target.value as PaymentStatus)}>
            {(Object.keys(PAYMENT_STATUS) as PaymentStatus[]).map((paymentStatus) => (
              <option key={paymentStatus} value={paymentStatus}>
                {PAYMENT_STATUS[paymentStatus].label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("payments.form.period")}>
          <input className={fieldInputClass} value={period} onChange={(event) => setPeriod(event.target.value)} />
        </Field>
        <Field label={t("payments.form.dueDate")}>
          <input type="date" className={fieldInputClass} value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
        </Field>
      </div>
      <Field label={t("payments.form.note")}>
        <textarea className={fieldInputClass} rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
    </Modal>
  );
}
